import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Alert,
  Divider,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Cancel as RejectedIcon,
  UploadFile as UploadFileIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { BASE_URL } from "../../services/configUrls";

// ─── Helpers ────────────────────────────────────────────────────

/**
 * Derives display status label from the inner course status (string).
 *   "0" → "Not Uploaded"
 *   "1" → "Pending"
 *   "2" → "Verified"
 *   "3" → "Rejected"
 */
const getDisplayStatus = (innerStatus) => {
  switch (innerStatus) {
    case "0":
      return "Not Uploaded";
    case "1":
      return "Pending";
    case "2":
      return "Verified";
    case "3":
      return "Rejected";
    default:
      return "Unknown";
  }
};

/**
 * Returns the status chip colour scheme.
 */
const getStatusChipStyle = (innerStatus) => {
  switch (innerStatus) {
    case "0":
      return { color: "#555", bgColor: "#F0F0F0", icon: <UploadFileIcon sx={{ fontSize: 16 }} /> };
    case "1":
      return { color: "#F57F17", bgColor: "#FFFDE7", icon: <PendingIcon sx={{ fontSize: 16 }} /> };
    case "2":
      return { color: "#2E7D32", bgColor: "#E8F5E9", icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> };
    case "3":
      return { color: "#C62828", bgColor: "#FFEBEE", icon: <RejectedIcon sx={{ fontSize: 16 }} /> };
    default:
      return { color: "#757575", bgColor: "#F5F5F5", icon: null };
  }
};

/**
 * Determines the upload field type that should be shown for a course.
 *
 * Returns:
 *   "pdf"  – show PDF file upload
 *   "url"  – show URL text input
 *   null   – no field (pending / verified)
 *
 * ─── Full Condition Matrix ───────────────────────────────────────
 *
 * outer=2 (int) && is_url="0" && inner="2" → status: "verified"     → field: null
 * outer=2 (int) && is_url="1" && inner="2" → status: "verified"     → field: null
 * outer=2 (int) && is_url="0" && inner="1" → status: "pending"      → field: null
 * outer=2 (int) && is_url="1" && inner="1" → status: "pending"      → field: null
 * outer=2 (int) && is_url="0" && inner="0" → status: "not upload"   → field: "pdf"
 * outer=2 (int) && is_url="1" && inner="0" → status: "not upload"   → field: "url"
 * outer=2 (int) && is_url="0" && inner="3" → status: "rejected"     → field: "pdf"
 * outer=2 (int) && is_url="1" && inner="3" → status: "rejected"     → field: "url"
 *
 * outer=1 (int) && inner="1"               → status: "pending"      → field: null
 * outer=1 (int) && inner="0"               → status: "not upload"   → field: "url"
 * outer=1 (int) && inner="2"               → status: "verified"     → field: null
 * outer=1 (int) && inner="3"               → status: "rejected"     → field: "url"
 *
 * outer=0 (int) && inner="1"               → status: "pending"      → field: null
 * outer=0 (int) && inner="0"               → status: "not upload"   → field: "pdf"
 * outer=0 (int) && inner="2"               → status: "verified"     → field: null
 * outer=0 (int) && inner="3"               → status: "rejected"     → field: "pdf"
 */
const getFieldType = (outerStatus, course) => {
  const innerStatus = course.status;
  const isUrl = course.is_url;

  // ── Verified (inner="2") → always null, no field shown ──
  if (innerStatus === "2") {
    return null;
  }

  // ── Pending (inner="1") → always null, no field shown ──
  if (innerStatus === "1") {
    return null;
  }

  // ── Not Uploaded (inner="0") or Rejected (inner="3") → show field ──

  if (outerStatus === 2) {
    // outer=2: field type depends on is_url
    //   is_url="0" → pdf upload
    //   is_url="1" → url upload
    return isUrl === "1" ? "url" : "pdf";
  }

  if (outerStatus === 1) {
    // outer=1: always url upload
    return "url";
  }

  if (outerStatus === 0) {
    // outer=0: always pdf upload
    return "pdf";
  }

  // Fallback
  return null;
};

// ─── Component ──────────────────────────────────────────────────

const UploadCertificate = () => {
  const location = useLocation();
  console.log(location);
  const eventData = location.state?.eventData;

  const navigate = useNavigate();

  // Passed via navigation state from FdpOngoing
  // const eventData = location.state?.eventData || null;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [apiData, setApiData] = useState(null);
  const [apiMessage, setApiMessage] = useState(""); // message from backend when all submitted
  const [uploadValues, setUploadValues] = useState({}); // { courseId: file | urlString }
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Refs for hidden file inputs
  const fileInputRefs = useRef({});

  useEffect(() => {
    getCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCourses = async () => {
    try {
      setLoading(true);
      const domainId = eventData?.domain_id;
      const bookslotId = eventData?.bookslot_id;

      const response = await axios.get(
        `${BASE_URL}/event/course`,
        {
          params: { domain_id: domainId, bookslot_id: bookslotId },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      // If backend returns a message (all submitted), show it
      if (response.data?.message) {
        setApiMessage(response.data.message);
        setApiData(null);
        return;
      }

      // response.data should have { status, courses, all_verified }
      setApiMessage("");
      setApiData(response.data);
    } catch (err) {
      console.error("Error fetching courses:", err);
      setSnackbar({
        open: true,
        message: "Failed to load certificate data. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ── File / URL change handlers ──

  const handleFileSelect = (courseId) => {
    fileInputRefs.current[courseId]?.click();
  };

  const handleFileChange = (courseId, event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate size (max 5 MB)
      if (file.size > 5 * 1024 * 1024) {
        setSnackbar({ open: true, message: "File size must be under 5 MB.", severity: "warning" });
        return;
      }
      setUploadValues((prev) => ({ ...prev, [courseId]: file }));
    }
  };

  const handleUrlChange = (courseId, value) => {
    setUploadValues((prev) => ({ ...prev, [courseId]: value }));
  };

  const handleRemoveFile = (courseId) => {
    setUploadValues((prev) => {
      const copy = { ...prev };
      delete copy[courseId];
      return copy;
    });
    // Reset file input
    if (fileInputRefs.current[courseId]) {
      fileInputRefs.current[courseId].value = "";
    }
  };

  // ── Helper to convert File to base64 (omitting the Data URL prefix) ──
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  // ── Submit handler (submits all courses at once in bulk) ──

  const handleSubmitAll = async () => {
    const courses = apiData?.courses || [];
    const outerStatus = apiData?.status;

    // Collect courses that have a value to submit
    const toSubmit = courses.filter((course) => {
      const fieldType = getFieldType(outerStatus, course);
      return fieldType && uploadValues[course.course_id];
    });

    if (toSubmit.length === 0) {
      setSnackbar({ open: true, message: "Please fill in at least one certificate before submitting.", severity: "warning" });
      return;
    }

    try {
      // Set submitting status to true for all courses being submitted
      const submittingMap = {};
      toSubmit.forEach((course) => {
        submittingMap[course.course_id] = true;
      });
      setSubmitting(submittingMap);

      const dataPayload = [];
      for (const course of toSubmit) {
        const fieldType = getFieldType(outerStatus, course);
        const val = uploadValues[course.course_id];

        let fileBase64 = null;
        let textUrl = null;

        if (fieldType === "pdf") {
          fileBase64 = await fileToBase64(val);
        } else if (fieldType === "url") {
          textUrl = val;
        }

        dataPayload.push({
          course_name: course.course_name,
          domain_id: Number(course.domain_id),
          course_id: Number(course.course_id),
          text: textUrl,
          file: fileBase64,
        });
      }

      const response = await fetch(`${BASE_URL}/event/certificate_upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({ bookslot_id: eventData?.bookslot_id, data: dataPayload }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message);
      }
      // decide color based on backend message
      let severity = "success";
      if (
        result.message?.toLowerCase().includes("upload processed")
      ) {
        severity = "warning";
      }

      setSnackbar({
        open: true,
        message: result.message,
        severity: severity,
      });

      // Clear the local values & input refs
      setUploadValues((prev) => {
        const copy = { ...prev };
        toSubmit.forEach((course) => {
          delete copy[course.course_id];
          if (fileInputRefs.current[course.course_id]) {
            fileInputRefs.current[course.course_id].value = "";
          }
        });
        return copy;
      });

      // Refresh data
      getCourses();
    } catch (error) {
      console.error("Error uploading certificates:", error);
      setSnackbar({ open: true, message: "Upload failed. Please try again.", severity: "error" });
    } finally {
      setSubmitting({});
    }
  };

  // ── Check if any submit is in progress ──
  const isAnySubmitting = Object.values(submitting).some(Boolean);

  // ── Render ──

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress sx={{ color: "#1976d2" }} />
      </Box>
    );
  }

  const outerStatus = apiData?.status; // integer (0, 1, 2)
  const courses = apiData?.courses || [];
  const domainName = eventData?.domain_name || "";

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F5F7FA" }}>
      {/* ── Blue Header Bar ── */}
      <Box
        sx={{
          backgroundColor: "#fff",
          color: "#060606ff",
          px: 3,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <IconButton
          onClick={() => {
            const tab = location.state?.fromTab || "ongoing";
            navigate(`/faculty-events?tab=${tab}`);
          }}
          size="small"
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {domainName}
        </Typography>
      </Box>

      {/* ── Page Content ── */}
      <Box sx={{ maxWidth: 900, mx: "auto", p: 3 }}>
        {/* ── Title Section ── */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#1F2937",
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
            }}
          >
            🏅 Credential verification
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280" }}>
            Upload your course certificates for verification.
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* ── API message (all submitted & verified) ── */}
        {apiMessage && (
          <Paper
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: "14px",
              border: "1px solid #C8E6C9",
              backgroundColor: "#E8F5E9",
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 40, color: "#2E7D32", mb: 1 }} />
            <Typography variant="body1" sx={{ color: "#2E7D32", fontWeight: 600 }}>
              {apiMessage}
            </Typography>
          </Paper>
        )}

        {/* ── No courses ── */}
        {!apiMessage && courses.length === 0 && (
          <Paper
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: "14px",
              border: "1px solid #E5E7EB",
            }}
          >
            <Typography variant="body1" sx={{ color: "#6B7280" }}>
              No certificates found for this event.
            </Typography>
          </Paper>
        )}

        {/* ── Course cards ── */}
        {courses.map((course) => {
          const fieldType = getFieldType(outerStatus, course);
          const statusLabel = getDisplayStatus(course.status);
          const chipStyle = getStatusChipStyle(course.status);
          const currentValue = uploadValues[course.course_id];

          return (
            <Paper
              key={course.course_id}
              sx={{
                p: 2.5,
                mb: 2,
                borderRadius: "12px",
                border: "1px solid #E5E7EB",
                boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
              }}
            >
              {/* ── Course Name & Status ── */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                  mb: (fieldType || (course.status === "3" && course.reason)) ? 2 : 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                  <FileIcon sx={{ fontSize: 20, color: "#1976d2" }} />
                  <Typography
                    sx={{ fontWeight: 600, fontSize: "14px", color: "#1F2937" }}
                  >
                    {course.course_name}
                  </Typography>
                </Box>

                <Chip
                  label={statusLabel}
                  size="small"
                  variant="outlined"
                  sx={{
                    color: chipStyle.color,
                    borderColor: chipStyle.color,
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    borderRadius: "6px",
                    height: 26,
                  }}
                />
              </Box>

              {/* ── Rejection Reason ── */}
              {course.status === "3" && course.reason && (
                <Box sx={{ mb: fieldType ? 2 : 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#C62828",
                      fontWeight: 500,
                      fontSize: "13px",
                      backgroundColor: "#FFEBEE",
                      p: 1.5,
                      borderRadius: "8px",
                      border: "1px solid #FFCDD2",
                    }}
                  >
                    Rejected: {course.reason}
                  </Typography>
                </Box>
              )}

              {/* ── Upload Field (conditional) ── */}
              {fieldType === "pdf" && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    fontSize="13px"
                    fontWeight={600}
                    sx={{ color: "#374151", mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <CloudUploadIcon sx={{ fontSize: 16, color: "#6B7280" }} />
                    Upload Certificate
                  </Typography>

                  {/* Hidden file input */}
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    style={{ display: "none" }}
                    ref={(el) => (fileInputRefs.current[course.course_id] = el)}
                    onChange={(e) => handleFileChange(course.course_id, e)}
                  />

                  {/* Compact drop-zone */}
                  {!currentValue ? (
                    <Box
                      onClick={() => handleFileSelect(course.course_id)}
                      sx={{
                        border: "1px dashed #CBD5E1",
                        borderRadius: "10px",
                        p: 1.5,
                        textAlign: "center",
                        cursor: "pointer",
                        backgroundColor: "#FAFBFC",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: "#1976d2",
                          backgroundColor: "#EBF5FF",
                        },
                      }}
                    >
                      <CloudUploadIcon sx={{ fontSize: 24, color: "#94A3B8", mb: 0.5 }} />
                      <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 500, fontSize: "13px" }}>
                        Click to select a certificate file
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: "10px",
                        backgroundColor: "#F0F9FF",
                        border: "1px solid #BAE6FD",
                      }}
                    >
                      <FileIcon sx={{ color: "#0284C7", fontSize: 24 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: "#0C4A6E", fontSize: "13px" }}>
                          {currentValue.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#7DD3FC" }}>
                          {(currentValue.size / 1024).toFixed(1)} KB
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => handleRemoveFile(course.course_id)}>
                        <DeleteIcon sx={{ fontSize: 18, color: "#EF4444" }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}

              {fieldType === "url" && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    fontSize="13px"
                    fontWeight={600}
                    sx={{ color: "#374151", mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <LinkIcon sx={{ fontSize: 16, color: "#6B7280" }} />
                    Certificate URL
                  </Typography>

                  <TextField
                    fullWidth
                    size="small"
                    placeholder="https://..."
                    value={currentValue || ""}
                    onChange={(e) => handleUrlChange(course.course_id, e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        backgroundColor: "#fff",
                        fontSize: "14px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#E0E0E0",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1976d2",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1976d2",
                        },
                      },
                    }}
                  />
                </Box>
              )}

              {/* ── No field message for pending / verified ── */}
              {fieldType === null && course.status === "1" && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: "10px",
                    backgroundColor: "#FFFDE7",
                    border: "1px solid #FFF9C4",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <PendingIcon sx={{ color: "#F57F17", fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: "#F57F17", fontWeight: 500 }}>
                    Your certificate is under review. Please wait for verification.
                  </Typography>
                </Box>
              )}

              {fieldType === null && course.status === "2" && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: "10px",
                    backgroundColor: "#E8F5E9",
                    border: "1px solid #C8E6C9",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CheckCircleIcon sx={{ color: "#2E7D32", fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: "#2E7D32", fontWeight: 500 }}>
                    Certificate verified!
                  </Typography>
                </Box>
              )}
            </Paper>
          );
        })}

        {/* ── Submit Certificates Button (centered, full-width) ── */}
        {courses.some((c) => getFieldType(outerStatus, c) !== null) && (
          <Button
            variant="contained"
            fullWidth
            disabled={isAnySubmitting}
            onClick={handleSubmitAll}
            startIcon={
              isAnySubmitting ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : (
                <SendIcon />
              )
            }
            sx={{
              mt: 2,
              py: 1.5,
              borderRadius: "10px",
              textTransform: "none",
              backgroundColor: "#1976d2",
              fontSize: "15px",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#1565c0" },
              "&:disabled": { backgroundColor: "#B0BEC5" },
            }}
          >
            {isAnySubmitting ? "Submitting..." : "Submit Certificates"}
          </Button>
        )}
      </Box>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: "10px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UploadCertificate;
